/**
 * LiveResolve: try the selected enquiry line again, with other wording if wanted.
 *
 * Re-resolves one line against the case's business area via `POST /api/resolve`
 * (`useResolveLine`) and renders the result: outcome, the area-scoped `item_ref`,
 * the calibrated confidence (normalised 0..1 to % in one place,
 * `toConfidencePercent`), and the evidence chain.
 *
 * Read-only: `/api/resolve` never writes to Go Autonomous, the mailboxes or F&O,
 * and nothing is saved to the case. The three UI states are handled with DS
 * components only: a loading button, a `warning` Alert for the 503 "engine not
 * configured" case, and a `danger` Alert for any other transport/server failure.
 *
 * Mounted with `key={`live-${line.id}`}` by the page, so selecting a different
 * line remounts it: the input resets to that line and the previous result clears,
 * without a data-loading `useEffect`.
 */

import { Icon, RefreshCw, Target } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  DescriptionList,
  Field,
  Separator,
  Textarea,
  Typography,
} from "@norican/react";
import { useState } from "react";

import { ConfidenceMeter } from "@/components/confidence-meter";
import {
  type ApiError,
  type ResolveResponse,
  toConfidencePercent,
  useResolveLine,
} from "@/lib/api";
import { logger } from "@/lib/logger";

type OutcomeTone = "success" | "warning" | "danger" | "neutral";

const OUTCOME_TONE: Record<string, OutcomeTone> = {
  match: "success",
  clarify: "warning",
  handoff: "danger",
};

const OUTCOME_LABEL: Record<string, string> = {
  match: "Item found",
  clarify: "Needs a check",
  handoff: "No item found",
};

/** Format the area-scoped item reference, preferring the canonical `item_ref`. */
function formatItemRef(result: ResolveResponse): string {
  const ref = result.resolution?.item_ref ?? result.item_ref;
  if (ref) {
    return `${ref.area.toUpperCase()} · ${ref.item_id}`;
  }
  return result.item_id ?? "no match";
}

function GlassBox({ result }: { result: ResolveResponse }) {
  const outcome = result.resolution?.outcome ?? result.outcome;
  const ladderLevel = result.resolution?.ladder_level ?? result.ladder_level;
  // Canonical confidence is 0..1; normalise to the % the meter consumes, once.
  const confidencePct = toConfidencePercent(result.resolution?.confidence ?? result.confidence);
  const evidence = result.evidence ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={OUTCOME_TONE[outcome] ?? "neutral"}>{OUTCOME_LABEL[outcome] ?? outcome}</Badge>
        <Typography as="span" variant="code" className="text-fg font-bold">
          {formatItemRef(result)}
        </Typography>
        {result.auto_accept && (
          <Badge tone="success" variant="outline" size="sm">
            Matched without review
          </Badge>
        )}
        <span className="text-fg-subtle ml-auto text-xs font-semibold">
          Matching step {ladderLevel}
        </span>
      </div>

      <div>
        <Typography variant="caption" tone="subtle" className="mb-1 block">
          How sure
        </Typography>
        <ConfidenceMeter value={confidencePct} />
      </div>

      {result.reason && (
        <Typography variant="body-sm" tone="muted">
          {result.reason}
        </Typography>
      )}

      {evidence.length > 0 && (
        <>
          <Separator label="Evidence" />
          <DescriptionList
            orientation="horizontal"
            items={evidence.map((item, index) => ({
              label: (
                <span className="text-fg-subtle" key={`k-${index}`}>
                  {item.kind}
                </span>
              ),
              value: item.detail,
            }))}
          />
        </>
      )}
    </div>
  );
}

export interface LiveResolveProps {
  /** Business/legal-entity scope for the resolution, e.g. "in". */
  area: string;
  /** The enquiry line text to seed the input with. */
  initialLine: string;
}

export function LiveResolve({ area, initialLine }: LiveResolveProps) {
  const [line, setLine] = useState(initialLine);
  const resolve = useResolveLine();
  const trimmed = line.trim();

  const onResolve = () => {
    if (!trimmed) {
      return;
    }
    logger.debug("[case] live re-resolve", { area, line: trimmed });
    resolve.mutate({ area, line: trimmed });
  };

  const errorStatus = (resolve.error as ApiError | null)?.status;
  const isUnconfigured = errorStatus === 503;
  const isOtherError = resolve.isError && !isUnconfigured;

  return (
    <Card variant="outline" className="overflow-hidden">
      <Card.Header
        padding="sm"
        className="border-border-subtle flex items-center justify-between gap-2 border-b"
      >
        <span className="text-fg inline-flex items-center gap-2">
          <Icon icon={Target} size="sm" className="text-accent-fg-subtle" />
          <Typography as="span" variant="label" className="font-bold">
            Try this line again
          </Typography>
        </span>
        <Badge tone="info" variant="outline" size="sm">
          Read-only
        </Badge>
      </Card.Header>
      <Card.Body className="flex flex-col gap-3">
        <Typography variant="caption" tone="subtle">
          Change the wording and let I2Q look the line up again in the {area.toUpperCase()} item
          catalogue. Nothing is saved and nothing is sent.
        </Typography>

        <Field label="Customer asked">
          <Textarea
            value={line}
            onChange={(event) => setLine(event.target.value)}
            rows={2}
            placeholder="for example shot valve 2440D0010"
            aria-label="Line to look up"
          />
        </Field>

        <div className="flex items-center gap-3">
          <Button
            tone="accent"
            startIcon={<Icon icon={RefreshCw} size="sm" />}
            loading={resolve.isPending}
            disabled={!trimmed}
            onClick={onResolve}
          >
            Look up again
          </Button>
        </div>

        {isUnconfigured && (
          <Alert tone="warning" title="Lookup not available">
            I2Q is not available here, so it cannot look the line up again now.
          </Alert>
        )}

        {isOtherError && (
          <Alert tone="danger" title="Lookup failed">
            I2Q could not look this line up{errorStatus ? ` (HTTP ${errorStatus})` : ""}. Try again
            in a moment.
          </Alert>
        )}

        {resolve.data && <GlassBox result={resolve.data} />}
      </Card.Body>
    </Card>
  );
}
